<template>
    <Teleport to="body">
    <!-- Cart Sidebar -->
    <div
        class="cart-sidebar fixed right-0 bottom-0 top-0 flex flex-col font-inter transition-all duration-300 z-[120] transform"
        :class="isOpen ? 'translate-x-0 shadow-2xl' : 'translate-x-full'"
    >
        <!-- Header -->
        <header class="flex items-center justify-between px-4 py-4 border-b border-gray-100 shrink-0">
            <button @click="goBack" class="p-1 transition duration-200 hover:opacity-60">
                <svg v-if="step === 1" viewBox="0 0 24 24" style="width:1.4rem;height:1.4rem"><path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/></svg>
                <svg v-else viewBox="0 0 24 24" style="width:1.4rem;height:1.4rem"><path fill="currentColor" d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z"/></svg>
            </button>
            <h2 class="text-sm font-bold uppercase tracking-wider">{{ stepTitle }}</h2>
            <button v-if="step === 1 && cart.length" @click="clearCart" class="text-sm font-semibold" style="color:var(--primary-color, #ef4444)">Limpar</button>
            <span v-else style="width:3rem"></span>
        </header>

        <!-- Step Indicator -->
        <div v-if="!isOnTab" class="flex items-center gap-2 px-4 py-2 border-b border-gray-50 shrink-0">
            <div class="flex-1 h-1 rounded-full transition-colors duration-200" :class="step >= 1 ? 'bg-gray-900' : 'bg-gray-200'"></div>
            <div class="flex-1 h-1 rounded-full transition-colors duration-200" :class="step >= 2 ? 'bg-gray-900' : 'bg-gray-200'"></div>
            <div class="flex-1 h-1 rounded-full transition-colors duration-200" :class="step >= 3 ? 'bg-gray-900' : 'bg-gray-200'"></div>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto">

        <!-- STEP 1 — Cart -->
        <div v-show="step === 1">
            <!-- Empty -->
            <div v-if="!cart.length" class="flex flex-col items-center justify-center text-center py-20 px-8">
                <div class="relative">
                    <div class="w-28 h-28 rounded-full bg-gray-50 flex items-center justify-center">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                            <line x1="3" y1="6" x2="21" y2="6"/>
                            <path d="M16 10a4 4 0 0 1-8 0"/>
                        </svg>
                    </div>
                    <span class="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center text-white text-lg font-bold shadow-md" :style="{ background: 'var(--primary-color, #ef4444)', color: 'var(--sf-on-primary, #fff)' }">+</span>
                </div>
                <h3 class="font-bold text-base mt-5">Seu carrinho está vazio</h3>
                <p class="text-gray-500 text-sm mt-1 max-w-[260px]">Adicione produtos do cardápio para começar seu pedido</p>
                <button
                    @click="setIsOpen(false)"
                    class="mt-6 px-6 py-3 rounded-xl text-sm font-bold text-white transition hover:opacity-90"
                    :style="{ background: 'var(--primary-color, #ef4444)', color: 'var(--sf-on-primary, #fff)' }"
                >
                    Ver cardápio
                </button>
            </div>

            <!-- Restaurant Info -->
            <div v-if="cart.length" class="flex items-center gap-3 px-4 py-3 border-b border-gray-50">
                <img
                    v-if="restaurant.thumbnail || designCustomLogo"
                    :src="restaurantLogo"
                    @error="($event.target as HTMLImageElement).src = '/assets/no-image.png'"
                    class="w-12 h-12 rounded-full object-cover border border-gray-100"
                    alt=""
                />
                <div v-else class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#737373" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/>
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                        <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/>
                        <path d="M2 7h20"/>
                        <path d="M22 7v3a2 2 0 0 1-2 2 2.7 2.7 0 0 1-2.6-1.9 2.7 2.7 0 0 1-5.2 0 2.7 2.7 0 0 1-5.2 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7"/>
                    </svg>
                </div>
                <div class="flex-1 min-w-0">
                    <h3 class="font-bold text-sm truncate">{{ restaurant.name }}</h3>
                    <button class="text-xs font-semibold mt-0.5" style="color:var(--primary-color, #ef4444)" @click="setIsOpen(false)">Adicionar mais itens</button>
                </div>
            </div>

            <!-- Tab (Comanda) Banner -->
            <div v-if="isOnTab && cart.length" class="px-4 py-3 border-b border-gray-50">
                <div class="rounded-2xl p-4 border border-gray-100 bg-gradient-to-br from-gray-50 to-white">
                    <div class="flex items-start gap-3">
                        <div class="w-11 h-11 rounded-full flex items-center justify-center shrink-0" :style="{ background: 'color-mix(in srgb, var(--primary-color, #ef4444) 12%, transparent)', color: 'var(--primary-color, #ef4444)' }">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <rect x="3" y="3" width="18" height="6" rx="1"/>
                                <path d="M5 9v12M19 9v12M5 15h14"/>
                            </svg>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-[10px] font-bold uppercase tracking-wider text-gray-500">Comanda aberta</p>
                            <p class="text-base font-bold leading-tight mt-0.5">Mesa #{{ tableNumber }}</p>
                            <p class="text-xs text-gray-500 mt-1">O pedido será adicionado à conta da mesa. O pagamento é feito ao final.</p>
                        </div>
                    </div>
                    <button
                        @click="leaveTab"
                        class="w-full mt-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition flex items-center justify-center gap-2"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M15 12H3M10 7l-5 5 5 5M14 5h5a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Sair da comanda
                    </button>
                </div>
            </div>

            <!-- Items -->
            <div v-if="cart.length" class="px-4 py-3 space-y-3">
                <h4 class="text-xs font-bold uppercase tracking-wider text-gray-600">Itens adicionados</h4>
                <div v-for="(product, index) in cart" :key="product.id + '-' + index" class="flex gap-3 pb-3 border-b border-gray-50 last:border-b-0">
                    <img
                        :src="getCartItemImage(product)"
                        @error="($event.target as HTMLImageElement).src = '/assets/no-image.png'"
                        class="w-16 h-16 rounded-lg object-cover bg-gray-50 shrink-0"
                        alt=""
                    />
                    <div class="flex-1 min-w-0">
                        <h5 class="text-sm font-semibold leading-tight">{{ product.name }}</h5>
                        <button
                            v-if="hasCustomizations(product)"
                            @click="product.seeMore = !product.seeMore"
                            class="text-xs text-gray-500 hover:underline mt-1 flex items-center gap-1"
                        >
                            <span>{{ product.seeMore ? 'Ocultar detalhes' : 'Ver detalhes' }}</span>
                            <svg :class="product.seeMore ? 'rotate-180' : ''" class="transform transition-transform" width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M16.5999 7.45801L11.1666 12.8913C10.5249 13.533 9.4749 13.533 8.83324 12.8913L3.3999 7.45801" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </button>
                        <p class="text-sm font-bold mt-1" style="color:var(--primary-color, #ef4444)">{{ getProductPrice(product).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</p>

                        <!-- Quantity Controls -->
                        <div class="flex items-center justify-between mt-2 gap-2">
                            <div class="flex items-center bg-gray-50 rounded-full">
                                <button @click="product.quantity--; product.quantity <= 0 ? cart.splice(index, 1) : ''" class="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-900">
                                    <svg viewBox="0 0 24 24" style="width:1rem;height:1rem"><path fill="currentColor" d="M19,13H5V11H19V13Z"/></svg>
                                </button>
                                <span class="text-sm font-bold w-6 text-center">{{ product.quantity }}</span>
                                <button @click="product.quantity++" class="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-900">
                                    <svg viewBox="0 0 24 24" style="width:1rem;height:1rem"><path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/></svg>
                                </button>
                            </div>
                            <button @click="cart.splice(index, 1)" class="text-gray-400 hover:text-red-500 transition-colors p-1">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21 5.97998C17.67 5.64998 14.32 5.47998 10.98 5.47998C9 5.47998 7.02 5.57998 5.04 5.77998L3 5.97998M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97M18.8499 9.13989L18.1999 19.2099C18.0899 20.7799 17.9999 21.9999 15.2099 21.9999H8.7899C5.9999 21.9999 5.9099 20.7799 5.7999 19.2099L5.1499 9.13989" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </button>
                        </div>

                        <!-- Variations + Add-ons (expanded) -->
                        <div v-if="product.seeMore && hasCustomizations(product)" class="mt-2 space-y-1 text-xs text-gray-500">
                            <template v-for="(variation, vIdx) in normalizeVariations(product.variations)" :key="vIdx">
                                <div v-if="variation.values?.label?.length">
                                    <span class="font-semibold text-gray-600">{{ variation.name }}:</span>
                                    <span class="ml-1">{{ variation.values.label.join(', ') }}</span>
                                </div>
                            </template>
                            <template v-for="(group, agIdx) in (product.addonGroups || product.addon_groups || [])" :key="agIdx">
                                <div v-if="group.addons?.length">
                                    <div class="font-semibold text-gray-600">{{ group.name }}:</div>
                                    <div v-for="(addon, aIdx) in group.addons" :key="aIdx" class="ml-2">
                                        {{ addon.quantity * product.quantity }}x {{ addOns[addon.id]?.name }}
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Coupon -->
            <div v-if="cart.length" class="px-4 py-3 border-t border-gray-50">
                <button
                    @click="openCouponForm = !openCouponForm"
                    class="w-full flex items-center justify-between gap-2 py-2"
                >
                    <div class="flex items-center gap-3">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 4A2 2 0 0 0 2 6V10C3.11 10 4 10.9 4 12A2 2 0 0 1 2 14V18A2 2 0 0 0 4 20H20A2 2 0 0 0 22 18V14A2 2 0 0 1 20 12C20 10.89 20.9 10 22 10V6C22 4.89 21.1 4 20 4H4Z" stroke="currentColor" stroke-width="1.5"/></svg>
                        <div class="text-left">
                            <p class="text-sm font-semibold">Cupom</p>
                            <p v-if="appliedCoupon" class="text-xs font-bold" style="color:var(--primary-color, #ef4444)">{{ appliedCoupon.code }}</p>
                            <p v-else class="text-xs text-gray-500">Digite um código</p>
                        </div>
                    </div>
                    <span class="text-sm font-semibold" style="color:var(--primary-color, #ef4444)">{{ openCouponForm ? 'Fechar' : (appliedCoupon ? 'Alterar' : 'Digitar') }}</span>
                </button>
                <form v-if="openCouponForm" @submit.prevent="applyCoupon" class="flex gap-2 mt-2">
                    <input v-model="couponCode" type="text" placeholder="ex: CUPOM30" class="flex-1 bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400" />
                    <button type="submit" class="px-4 bg-gray-900 text-white rounded-lg text-sm font-semibold">Aplicar</button>
                </form>
            </div>

            <!-- Notes -->
            <div v-if="cart.length" class="px-4 py-3 border-t border-gray-50">
                <label class="block text-sm font-semibold mb-2">Observações do pedido</label>
                <textarea
                    v-model="notes"
                    rows="2"
                    maxlength="255"
                    placeholder="Ex.: sem cebola, ponto da carne..."
                    class="w-full bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400 resize-none"
                ></textarea>
            </div>

            <!-- Summary -->
            <div v-if="cart.length" class="px-4 py-3 border-t border-gray-50">
                <h4 class="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Resumo de valores</h4>
                <div class="space-y-1.5 text-sm">
                    <div class="flex justify-between">
                        <span class="text-gray-600">Subtotal</span>
                        <span>{{ totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                    <div v-if="couponDiscount > 0" class="flex justify-between text-emerald-600 font-medium">
                        <span>Desconto</span>
                        <span>- {{ couponDiscount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                    <div class="flex justify-between font-bold text-base pt-2 border-t border-gray-100">
                        <span>Total</span>
                        <span>{{ (totalPrice - couponDiscount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                </div>
            </div>

            <div class="h-24"></div>
        </div>

        <!-- STEP 2 — Delivery -->
        <div v-show="step === 2">
            <!-- Delivery Type -->
            <div class="px-4 py-4 border-b border-gray-50">
                <h4 class="text-xs font-bold uppercase tracking-wider text-gray-600 mb-3">Como você quer receber?</h4>
                <div class="space-y-2">
                    <button
                        v-for="type in deliveryTypes"
                        :key="type.value"
                        @click="selectDeliveryType(type.value)"
                        class="w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-150 text-left"
                        :class="deliveryType === type.value ? 'border-gray-900 bg-gray-50' : 'border-gray-100 hover:border-gray-200'"
                    >
                        <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" :class="deliveryType === type.value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'">
                            <svg v-if="type.icon === 'delivery'" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="5" cy="17" r="3"/>
                                <circle cx="19" cy="17" r="3"/>
                                <path d="M5 17 8 10h7l4 7"/>
                                <path d="M10 10v3h4v-3"/>
                                <path d="M8 10V7h3"/>
                            </svg>
                            <svg v-else-if="type.icon === 'pickup'" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 11V8a3 3 0 0 1 6 0v3M5 11h14l-1 10H6L5 11z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </div>
                        <div class="flex-1">
                            <p class="text-sm font-bold">{{ type.label }}</p>
                            <p class="text-xs text-gray-500 mt-0.5">{{ type.description }}</p>
                        </div>
                        <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center" :class="deliveryType === type.value ? 'border-gray-900' : 'border-gray-300'">
                            <div v-if="deliveryType === type.value" class="w-2.5 h-2.5 rounded-full bg-gray-900"></div>
                        </div>
                    </button>
                </div>
            </div>

            <!-- Address (delivery only) -->
            <div v-if="deliveryType === 'delivery'" class="px-4 py-4 border-b border-gray-50">
                <div class="flex items-center justify-between mb-3">
                    <h4 class="text-xs font-bold uppercase tracking-wider text-gray-600">Endereço de entrega</h4>
                    <button v-if="deliveryAddress.street && !showAddressForm" @click="openAddressSearch" class="text-sm font-semibold" style="color:var(--primary-color, #ef4444)">Trocar</button>
                </div>

                <!-- Selected address card with mini Leaflet map preview -->
                <div v-if="deliveryAddress.street && !showAddressForm" class="rounded-2xl overflow-hidden border border-gray-100 bg-white">
                    <div v-if="deliveryAddress.lat && deliveryAddress.lng" class="relative h-32 bg-gray-100">
                        <div ref="cartMapEl" class="absolute inset-0"></div>
                        <div class="absolute inset-0 pointer-events-none flex items-center justify-center z-[5]">
                            <svg width="32" height="40" viewBox="0 0 40 48" fill="none" class="-mt-3 drop-shadow-md">
                                <path d="M20 0C8.95 0 0 8.95 0 20c0 14 20 28 20 28s20-14 20-28C40 8.95 31.05 0 20 0z" fill="var(--primary-color, #ef4444)"/>
                                <circle cx="20" cy="20" r="6.5" fill="#fff"/>
                            </svg>
                        </div>
                    </div>
                    <div class="flex gap-3 items-start p-3">
                        <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0" v-if="!(deliveryAddress.lat && deliveryAddress.lng)">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 13.43a3.12 3.12 0 1 0 0-6.24 3.12 3.12 0 0 0 0 6.24z" stroke="#525252" stroke-width="1.5"/><path d="M3.62 8.49C5.59 -.16 18.42 -.15 20.38 8.5c1.15 5.07-2 9.36-4.77 12.01a5.2 5.2 0 0 1-7.22 0C5.62 17.85 2.47 13.56 3.62 8.49z" stroke="#525252" stroke-width="1.5"/></svg>
                        </div>
                        <div class="flex-1 text-sm min-w-0">
                            <p class="font-semibold">{{ deliveryAddress.street }}, {{ deliveryAddress.number }}</p>
                            <p v-if="deliveryAddress.complement" class="text-gray-500 text-xs">{{ deliveryAddress.complement }}</p>
                            <p class="text-gray-500 text-xs">{{ deliveryAddress.neighborhood }} - {{ deliveryAddress.city }}/{{ deliveryAddress.state }}</p>
                            <p v-if="deliveryAddress.zipCode" class="text-gray-400 text-xs mt-0.5">CEP: {{ deliveryAddress.zipCode }}</p>
                            <p v-if="deliveryAddress.reference" class="text-gray-400 text-xs italic mt-0.5">Ref: {{ deliveryAddress.reference }}</p>
                        </div>
                    </div>
                </div>

                <button
                    v-else-if="!showAddressForm"
                    @click="openAddressSearch"
                    class="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors text-left"
                >
                    <div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" :style="{ background: 'color-mix(in srgb, var(--primary-color, #ef4444) 12%, transparent)', color: 'var(--primary-color, #ef4444)' }">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.8"/>
                            <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <div class="flex-1">
                        <p class="text-sm font-semibold">Buscar endereço</p>
                        <p class="text-xs text-gray-500">Digite para encontrar no mapa</p>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-gray-300">
                        <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>

                <div v-if="showAddressForm" class="space-y-2 mt-2">
                    <div class="grid grid-cols-3 gap-2">
                        <input v-model="deliveryAddress.zipCode" type="text" placeholder="CEP" class="col-span-1 bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400" @blur="fetchAddressByCep" />
                        <input v-model="deliveryAddress.state" type="text" placeholder="Estado" maxlength="2" class="col-span-1 bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400" />
                        <input v-model="deliveryAddress.city" type="text" placeholder="Cidade" class="col-span-1 bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400" />
                    </div>
                    <input v-model="deliveryAddress.street" type="text" placeholder="Rua / Avenida" class="w-full bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400" />
                    <div class="grid grid-cols-3 gap-2">
                        <input v-model="deliveryAddress.number" type="text" placeholder="Nº" class="col-span-1 bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400" />
                        <input v-model="deliveryAddress.complement" type="text" placeholder="Complemento" class="col-span-2 bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400" />
                    </div>
                    <input v-model="deliveryAddress.neighborhood" type="text" placeholder="Bairro" class="w-full bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400" />
                    <input v-model="deliveryAddress.reference" type="text" placeholder="Ponto de referência (opcional)" class="w-full bg-gray-50 p-3 rounded-lg text-sm border border-gray-100 focus:outline-none focus:border-gray-400" />
                    <div class="flex gap-2 pt-1">
                        <button v-if="deliveryAddress.street" @click="showAddressForm = false" class="flex-1 py-3 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600">Cancelar</button>
                        <button @click="saveAddress" class="flex-1 py-3 bg-gray-900 text-white rounded-lg text-sm font-bold">Salvar endereço</button>
                    </div>
                </div>
            </div>

            <!-- Order Timing -->
            <div class="px-4 py-4 border-b border-gray-50">
                <h4 class="text-xs font-bold uppercase tracking-wider text-gray-600 mb-3">Quando?</h4>
                <div class="grid grid-cols-2 gap-2">
                    <button
                        @click="orderTiming = 'immediate'"
                        class="p-3 rounded-xl border-2 transition-all duration-150 text-sm font-bold"
                        :class="orderTiming === 'immediate' ? 'border-gray-900 bg-gray-50 text-gray-900' : 'border-gray-100 text-gray-500 hover:border-gray-200'"
                    >
                        Para agora
                    </button>
                    <button
                        @click="enableScheduled"
                        class="p-3 rounded-xl border-2 transition-all duration-150 text-sm font-bold"
                        :class="orderTiming === 'scheduled' ? 'border-gray-900 bg-gray-50 text-gray-900' : 'border-gray-100 text-gray-500 hover:border-gray-200'"
                    >
                        Agendar
                    </button>
                </div>

                <div v-if="orderTiming === 'scheduled'" class="mt-4 space-y-4">
                    <!-- No availability warning -->
                    <div v-if="!availableDays.length" class="p-3 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-800 flex items-start gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" class="shrink-0 mt-0.5">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/>
                            <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                        <span>Nenhum dia disponível para agendamento nos próximos 7 dias.</span>
                    </div>

                    <!-- Day picker (horizontal carousel with peek hint) -->
                    <div v-if="availableDays.length">
                        <label class="text-xs font-semibold text-gray-500 mb-2 block">Dia</label>
                        <div class="day-scroller-wrapper relative -mx-4">
                            <div
                                ref="dayScroller"
                                @scroll="updateDayScrollIndicators"
                                class="flex gap-2 overflow-x-auto px-4 pb-1 scroll-snap-x"
                            >
                                <button
                                    v-for="day in availableDays"
                                    :key="day.value"
                                    @click="scheduledDate = day.value"
                                    class="shrink-0 flex flex-col items-center justify-center w-16 h-20 rounded-xl border-2 transition-all duration-150"
                                    :class="scheduledDate === day.value ? 'border-gray-900 bg-gray-50' : 'border-gray-100 hover:border-gray-200'"
                                >
                                    <span class="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{{ day.weekday }}</span>
                                    <span class="text-xl font-bold mt-1">{{ day.day }}</span>
                                    <span class="text-[10px] text-gray-400">{{ day.month }}</span>
                                </button>
                                <!-- trailing spacer so the last card never sticks to the edge (gives the carousel feel) -->
                                <div class="shrink-0 w-4" aria-hidden="true"></div>
                            </div>
                            <!-- Right fade hint -->
                            <div
                                v-show="dayCanScrollRight"
                                class="pointer-events-none absolute top-0 bottom-1 right-0 w-10 bg-gradient-to-l from-white via-white/80 to-transparent"
                                aria-hidden="true"
                            ></div>
                            <!-- Left fade hint -->
                            <div
                                v-show="dayCanScrollLeft"
                                class="pointer-events-none absolute top-0 bottom-1 left-0 w-6 bg-gradient-to-r from-white via-white/80 to-transparent"
                                aria-hidden="true"
                            ></div>
                        </div>
                    </div>

                    <!-- Time incrementer (hour + minute) -->
                    <div v-if="scheduledDate">
                        <label class="text-xs font-semibold text-gray-500 mb-2 block">Horário</label>
                        <div class="flex items-center justify-center gap-3 bg-gray-50 rounded-xl p-3">
                            <!-- Hour -->
                            <div class="flex flex-col items-center gap-1">
                                <button
                                    @click="adjustTime('hour', 1)"
                                    :disabled="!canIncreaseTime || !scheduledTime"
                                    class="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-gray-200 disabled:opacity-30 hover:border-gray-400 transition-colors"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 15l5-5 5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                </button>
                                <span class="text-3xl font-bold w-14 text-center tabular-nums">{{ hourDisplay }}</span>
                                <button
                                    @click="adjustTime('hour', -1)"
                                    :disabled="!canDecreaseTime || !scheduledTime"
                                    class="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-gray-200 disabled:opacity-30 hover:border-gray-400 transition-colors"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 9l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                </button>
                            </div>
                            <span class="text-3xl font-bold pb-1">:</span>
                            <!-- Minute -->
                            <div class="flex flex-col items-center gap-1">
                                <button
                                    @click="adjustTime('minute', 5)"
                                    :disabled="!canIncreaseTime || !scheduledTime"
                                    class="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-gray-200 disabled:opacity-30 hover:border-gray-400 transition-colors"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 15l5-5 5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                </button>
                                <span class="text-3xl font-bold w-14 text-center tabular-nums">{{ minuteDisplay }}</span>
                                <button
                                    @click="adjustTime('minute', -5)"
                                    :disabled="!canDecreaseTime || !scheduledTime"
                                    class="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-gray-200 disabled:opacity-30 hover:border-gray-400 transition-colors"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 9l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                </button>
                            </div>
                        </div>
                        <p v-if="!scheduledTime" class="text-xs text-gray-400 mt-2 text-center">Selecione um horário</p>
                        <p v-else-if="dayOpenWindowLabel" class="text-[11px] text-gray-400 mt-2 text-center">
                            Horário de funcionamento: {{ dayOpenWindowLabel }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="h-24"></div>
        </div>

        <!-- STEP 3 — Payment -->
        <div v-show="step === 3">
            <!-- Section header -->
            <div class="px-4 pt-5 pb-3">
                <p class="text-xs font-bold uppercase tracking-wider" style="color:var(--primary-color, #ef4444)">Pagar na entrega</p>
                <p class="text-xs text-gray-500 mt-1">O pagamento será realizado quando o pedido chegar.</p>
            </div>

            <!-- Payment Method list -->
            <div class="px-4 pb-2 space-y-2">
                <button
                    v-for="method in paymentMethods"
                    :key="method.value"
                    @click="selectPaymentMethod(method.value)"
                    class="w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-150 text-left"
                    :class="paymentMethod === method.value ? 'border-gray-900 bg-gray-50' : 'border-gray-100 hover:border-gray-200'"
                >
                    <div class="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" :class="paymentMethodIconBg(method.value)">
                        <svg v-if="method.value === 'pix'" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M16.5098 5.01L14.5498 3.05C13.1498 1.65 10.8498 1.65 9.44977 3.05L7.48977 5.01C7.09977 5.4 7.09977 6.04 7.48977 6.43L11.2998 10.24C11.6898 10.63 12.3198 10.63 12.7098 10.24L16.5198 6.43C16.8998 6.04 16.8998 5.4 16.5098 5.01Z" fill="#32BCAD"/>
                            <path d="M5.01 7.49L3.05 9.45C1.65 10.85 1.65 13.15 3.05 14.55L5.01 16.51C5.4 16.9 6.03 16.9 6.42 16.51L10.23 12.7C10.62 12.31 10.62 11.68 10.23 11.29L6.43 7.49C6.04 7.1 5.4 7.1 5.01 7.49Z" fill="#32BCAD"/>
                            <path d="M20.95 9.45L18.99 7.49C18.6 7.1 17.97 7.1 17.58 7.49L13.77 11.3C13.38 11.69 13.38 12.32 13.77 12.71L17.58 16.52C17.97 16.91 18.6 16.91 18.99 16.52L20.95 14.56C22.35 13.15 22.35 10.85 20.95 9.45Z" fill="#32BCAD"/>
                            <path d="M7.49005 18.99L9.45005 20.95C10.8501 22.35 13.1501 22.35 14.5501 20.95L16.5101 18.99C16.9001 18.6 16.9001 17.97 16.5101 17.58L12.7001 13.77C12.3101 13.38 11.6801 13.38 11.2901 13.77L7.48005 17.58C7.10005 17.96 7.10005 18.6 7.49005 18.99Z" fill="#32BCAD"/>
                        </svg>
                        <svg v-else-if="method.value === 'credit_card'" width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M2 10h20" stroke="currentColor" stroke-width="1.5"/></svg>
                        <svg v-else-if="method.value === 'debit_card'" width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M2 10h20" stroke="currentColor" stroke-width="1.5"/><circle cx="7" cy="15" r="1.2" fill="currentColor"/></svg>
                        <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="12" r="2.5" stroke="currentColor" stroke-width="1.5"/></svg>
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-bold">{{ method.label }}</p>
                        <p class="text-xs text-gray-500 mt-0.5 truncate">{{ method.description }}</p>
                    </div>
                    <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0" :class="paymentMethod === method.value ? 'border-gray-900' : 'border-gray-300'">
                        <div v-if="paymentMethod === method.value" class="w-2.5 h-2.5 rounded-full bg-gray-900"></div>
                    </div>
                </button>
            </div>

            <!-- Change for Cash -->
            <div v-if="paymentMethod === 'cash'" class="mx-4 my-3 rounded-xl border border-emerald-100 bg-emerald-50/50 overflow-hidden">
                <label class="flex items-center justify-between p-3 cursor-pointer">
                    <div class="flex items-center gap-3">
                        <div class="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" stroke-width="1.6"/>
                                <circle cx="12" cy="12" r="2.5" stroke="currentColor" stroke-width="1.6"/>
                            </svg>
                        </div>
                        <div>
                            <p class="text-sm font-bold text-gray-900">Precisa de troco?</p>
                            <p class="text-xs text-gray-500 mt-0.5">Informe o valor que vai pagar</p>
                        </div>
                    </div>
                    <span class="relative inline-flex items-center shrink-0">
                        <input
                            type="checkbox"
                            :checked="changeAmount !== null"
                            @change="toggleChange(($event.target as HTMLInputElement).checked)"
                            class="sr-only peer"
                        />
                        <span class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white"></span>
                    </span>
                </label>
                <div v-if="changeAmount !== null" class="px-3 pb-3 pt-3 space-y-2 border-t border-emerald-100/70 bg-white/70">
                    <label class="text-xs text-gray-600 font-semibold">Troco para quanto?</label>
                    <div class="flex items-center gap-2 bg-white rounded-lg border border-emerald-200 px-3 py-2 focus-within:border-emerald-400 transition-colors">
                        <span class="text-sm text-gray-500 font-semibold">R$</span>
                        <input
                            :value="changeAmountDisplay"
                            @input="onChangeAmountInput"
                            type="tel"
                            inputmode="numeric"
                            placeholder="0,00"
                            class="flex-1 bg-transparent text-sm focus:outline-none font-semibold tabular-nums"
                        />
                    </div>
                    <p v-if="changeAmount && changeAmount > (totalPrice - couponDiscount)" class="text-xs text-emerald-600 font-semibold">
                        Troco a receber: {{ (changeAmount - (totalPrice - couponDiscount)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
                    </p>
                    <p v-else-if="changeAmount && changeAmount < (totalPrice - couponDiscount)" class="text-xs text-red-500 font-semibold">
                        Valor menor que o total do pedido
                    </p>
                </div>
            </div>

            <!-- Order Recap -->
            <div class="px-4 py-4 mt-2 border-t border-gray-50">
                <h4 class="text-xs font-bold uppercase tracking-wider text-gray-600 mb-3">Revisão do pedido</h4>
                <div class="space-y-2 text-sm">
                    <div class="flex items-center justify-between">
                        <span class="text-gray-500">Tipo</span>
                        <span class="font-semibold">{{ deliveryTypeLabel }}</span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-gray-500">Quando</span>
                        <span class="font-semibold">{{ orderTimingLabel }}</span>
                    </div>
                    <div v-if="deliveryType === 'delivery' && deliveryAddress.street" class="flex items-start justify-between gap-2">
                        <span class="text-gray-500 shrink-0">Endereço</span>
                        <span class="font-semibold text-right text-xs">{{ deliveryAddress.street }}, {{ deliveryAddress.number }}</span>
                    </div>
                    <div class="flex items-center justify-between pt-2 border-t border-gray-100">
                        <span class="text-gray-500">Subtotal</span>
                        <span>{{ totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                    <div v-if="couponDiscount > 0" class="flex items-center justify-between text-emerald-600 font-medium">
                        <span>Cupom {{ appliedCoupon?.code }}</span>
                        <span>- {{ couponDiscount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                    <div class="flex items-center justify-between font-bold text-base pt-2 border-t border-gray-100">
                        <span>Total</span>
                        <span>{{ (totalPrice - couponDiscount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                </div>
            </div>

            <div class="h-24"></div>
        </div>

        </div>

        <!-- Footer (sticky bottom) -->
        <div v-if="cart.length" class="cart-footer border-t border-gray-100 bg-white px-4 py-3 shrink-0">
            <div class="flex items-center justify-between mb-3">
                <div>
                    <p class="text-xs text-gray-500">Total</p>
                    <p class="text-xl font-bold">{{ (totalPrice - couponDiscount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</p>
                </div>
                <p class="text-xs text-gray-500">{{ cart.length }} {{ cart.length === 1 ? 'item' : 'itens' }}</p>
            </div>
            <button
                @click="onPrimaryAction"
                :disabled="!canProceed"
                class="w-full py-4 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-all duration-200"
                :class="canProceed ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-400 cursor-not-allowed'"
                :style="canProceed ? { background: 'var(--primary-color, #ef4444)', color: 'var(--sf-on-primary, #fff)' } : {}"
            >
                {{ primaryActionLabel }}
                <svg v-if="canProceed" width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M8.59 16.58L13.17 12L8.59 7.41L10 6L16 12L10 18L8.59 16.58Z" fill="currentColor"/></svg>
            </button>
            <p v-if="step === 1 && restaurant.minimumOrder > totalPrice" class="text-xs text-gray-500 text-center mt-2">
                Pedido mínimo: {{ restaurant.minimumOrder?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
            </p>
        </div>
    </div>

    <!-- Background overlay -->
    <div class="fixed inset-0 bg-black bg-opacity-50 z-[115]" v-if="isOpen" @click="setIsOpen(false)"></div>

    <CentralLoader :primaryColor="restaurant.design_custom?.primary_color" v-if="isPlacingOrder" :zIndex="999" />

    <AddressSearchModal
        :isOpen="isAddressSearchOpen"
        :savedAddresses="savedAddresses"
        @close="isAddressSearchOpen = false"
        @select="onAddressSelected"
        @manual="onAddressManual"
    />
    </Teleport>
</template>

<script lang="ts" setup>
import { watch, ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { cart, restaurant, addOns, deliveryType, paymentMethod, changeAmount, orderTiming, scheduledAt, cmFirebaseToken, apiBaseUrl, appliedCoupon } from '@/global/state/GlobalState'
import { customer, isAuthModalOpen } from '@/global/state/AuthState'
import axiosClient from '@/api/axios'
import { useToast } from 'vue-toastification'
import { CouponModel } from '@/models/CouponModel'
import CentralLoader from '@/components/loaders/CentralLoader.vue'
import { isQrcodeReaderOpen } from '@/global/state/ModalState'
import AddressSearchModal from '@/components/address/AddressSearchModal.vue'
import { loadLeaflet } from '@/composables/useLeafletMap'

const couponCode = ref<string>('')
const notes = ref<string>('')
const openCouponForm = ref<boolean>(false)
const isPlacingOrder = ref<boolean>(false)
const toast = useToast()
const showAddressForm = ref(false)
const step = ref<1 | 2 | 3>(1)
const tableNumber = ref<string | null>(localStorage.getItem('table_number'))
const isOnTab = computed(() => {
    const v = tableNumber.value
    return !!v && !isNaN(parseInt(v))
})

function leaveTab() {
    if (!window.confirm('Sair da comanda? Você poderá escolher entrega/retirada e a forma de pagamento.')) return
    localStorage.removeItem('table_number')
    tableNumber.value = null
    step.value = 1
}

function onStorageChange(e: StorageEvent) {
    if (e.key === 'table_number') {
        tableNumber.value = e.newValue
    }
}

watch(isOnTab, (onTab) => {
    if (onTab && step.value !== 1) step.value = 1
})

const stepTitle = computed(() => {
    if (step.value === 1) return 'Carrinho'
    if (step.value === 2) return 'Entrega'
    return 'Pagamento'
})

/* global defineProps */
// eslint-disable-next-line no-undef
const props = defineProps({
    isOpen: { type: Boolean, required: true },
    setIsOpen: { type: Function, required: true },
    setIsAuthModalOpen: { type: Function, required: true }
})

const paymentMethods = [
    { value: 'pix', label: 'PIX', description: 'Pagamento instantâneo' },
    { value: 'credit_card', label: 'Cartão de crédito', description: 'Visa, Master, Elo, etc.' },
    { value: 'debit_card', label: 'Cartão de débito', description: 'Pagamento à vista' },
    { value: 'cash', label: 'Dinheiro', description: 'Pagamento na entrega' }
]

const enabledOrderTypes = computed<string[]>(() => {
    const fromConfig = (restaurant.value as any)?.restaurantConfig?.enabledOrderTypes
    if (Array.isArray(fromConfig) && fromConfig.length > 0) return fromConfig
    // Backward-compat: se o backend ainda não retornar, libera tudo
    return ['delivery', 'pick_up', 'waiter']
})

const deliveryTypes = computed(() => {
    const types: { value: string; label: string; description: string; icon: string }[] = []
    if (enabledOrderTypes.value.includes('delivery')) {
        types.push({ value: 'delivery', label: 'Entrega', description: 'Receba no endereço', icon: 'delivery' })
    }
    if (enabledOrderTypes.value.includes('pick_up')) {
        types.push({ value: 'balcao', label: 'Retirar na loja', description: 'Pegue no balcão', icon: 'pickup' })
    }
    if (enabledOrderTypes.value.includes('waiter')) {
        types.push({ value: 'presencial', label: 'No local', description: 'Estou na mesa', icon: 'waiter' })
    }
    return types
})

const designCustomLogo = computed(() => {
    return (restaurant.value as any)?.restaurantDesignCustom?.logo
})

const restaurantLogo = computed(() => {
    if ((restaurant.value as any)?.thumbnail) {
        return `${apiBaseUrl}/storage/${(restaurant.value as any).thumbnail}`
    }
    if (designCustomLogo.value) return designCustomLogo.value
    return '/assets/no-image.png'
})

function selectDeliveryType(value: string) {
    deliveryType.value = value
    localStorage.setItem('deliveryType', value)
}

function selectPaymentMethod(value: string) {
    paymentMethod.value = value
    localStorage.setItem('paymentMethod', value)
    if (value !== 'cash') {
        changeAmount.value = null
    }
}

function clearCart() {
    cart.value = []
    localStorage.removeItem('cart')
}

function hasCustomizations(product: any) {
    const variations = product.variations
    const hasVariations = typeof variations === 'string' ? JSON.parse(variations).length : (variations?.length || 0)
    const hasAddOnGroups = product.addonGroups?.length || product.addon_groups?.length
    return hasVariations || hasAddOnGroups
}

function normalizeVariations(variations: any) {
    if (!variations) return []
    try {
        return typeof variations === 'string' ? JSON.parse(variations) : variations
    } catch {
        return []
    }
}

function getCartItemImage(product: any) {
    if (product.smallImage?.path) return apiBaseUrl + '/storage/' + product.smallImage.path
    if (product.small_image?.path) return apiBaseUrl + '/storage/' + product.small_image.path
    if (product.media?.[0]?.source) {
        return product.media[0].source.startsWith('http')
            ? product.media[0].source
            : apiBaseUrl + '/storage/' + product.media[0].source
    }
    return '/assets/no-image.png'
}

const scheduledDate = ref<string>('')
const scheduledTime = ref<string>('')

watch([scheduledDate, scheduledTime], ([d, t]) => {
    scheduledAt.value = d && t ? `${d}T${t}:00` : null
})


function toLocalDateString(d: Date) {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

const weekdayShort = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const monthShort = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

const weekdayKeyByJsDay: Record<number, string> = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday'
}

function parseTimeToMinutes(t: string | undefined): number | null {
    if (!t || typeof t !== 'string') return null
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(t)
    if (!m) return null
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10)
}

function businessHoursForDate(dateStr: string): { isOpen: boolean; opensAt?: string; closesAt?: string } {
    if (!dateStr) return { isOpen: true }
    const [y, mo, dy] = dateStr.split('-').map(Number)
    const d = new Date(y, (mo || 1) - 1, dy || 1)
    const key = weekdayKeyByJsDay[d.getDay()]
    const bh = (restaurant.value as any)?.restaurantConfig?.businessHours
    if (!bh || typeof bh !== 'object') return { isOpen: true }
    const entry = bh[key]
    if (!entry || typeof entry !== 'object') return { isOpen: true }
    return {
        isOpen: !!entry.isOpen,
        opensAt: entry.opensAt,
        closesAt: entry.closesAt
    }
}

const availableDays = computed(() => {
    const days: { value: string; weekday: string; day: string; month: string; isOpen: boolean }[] = []
    const base = new Date()
    base.setHours(0, 0, 0, 0)
    for (let i = 0; i < 7; i++) {
        const d = new Date(base)
        d.setDate(base.getDate() + i)
        const value = toLocalDateString(d)
        const bh = businessHoursForDate(value)
        if (!bh.isOpen) continue
        days.push({
            value,
            weekday: i === 0 ? 'Hoje' : i === 1 ? 'Amanhã' : weekdayShort[d.getDay()],
            day: String(d.getDate()).padStart(2, '0'),
            month: monthShort[d.getMonth()],
            isOpen: true
        })
    }
    return days
})

const dayOpenWindow = computed(() => {
    const bh = businessHoursForDate(scheduledDate.value)
    if (!bh.isOpen) return { opens: 0, closes: 23 * 60 + 55 }
    const opens = parseTimeToMinutes(bh.opensAt) ?? 0
    const closes = parseTimeToMinutes(bh.closesAt) ?? (23 * 60 + 55)
    return { opens, closes }
})

const minTimeMinutes = computed(() => {
    if (!scheduledDate.value) return 0
    const window = dayOpenWindow.value
    const todayStr = toLocalDateString(new Date())
    if (scheduledDate.value === todayStr) {
        const now = new Date()
        const earliest = Math.ceil((now.getHours() * 60 + now.getMinutes() + 30) / 5) * 5
        return Math.max(window.opens, earliest)
    }
    return window.opens
})

const maxTimeMinutes = computed(() => dayOpenWindow.value.closes)

const dayOpenWindowLabel = computed(() => {
    if (!scheduledDate.value) return ''
    const bh = businessHoursForDate(scheduledDate.value)
    if (!bh.isOpen || !bh.opensAt || !bh.closesAt) return ''
    return `${bh.opensAt} às ${bh.closesAt}`
})

const currentTimeMinutes = computed(() => {
    if (!scheduledTime.value) return -1
    const [h, m] = scheduledTime.value.split(':').map(Number)
    return h * 60 + m
})

const canIncreaseTime = computed(() => currentTimeMinutes.value < maxTimeMinutes.value)
const canDecreaseTime = computed(() => currentTimeMinutes.value > minTimeMinutes.value)

const hourDisplay = computed(() => {
    if (!scheduledTime.value) return '--'
    return scheduledTime.value.split(':')[0]
})
const minuteDisplay = computed(() => {
    if (!scheduledTime.value) return '--'
    return scheduledTime.value.split(':')[1]
})

function setTimeFromMinutes(total: number) {
    total = Math.max(minTimeMinutes.value, Math.min(maxTimeMinutes.value, total))
    const h = Math.floor(total / 60)
    const m = total % 60
    scheduledTime.value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function adjustTime(unit: 'hour' | 'minute', delta: number) {
    if (!scheduledTime.value) {
        setTimeFromMinutes(minTimeMinutes.value)
        return
    }
    const next = currentTimeMinutes.value + (unit === 'hour' ? delta * 60 : delta)
    setTimeFromMinutes(next)
}

function enableScheduled() {
    orderTiming.value = 'scheduled'
    if (!scheduledDate.value) {
        const first = availableDays.value[0]
        scheduledDate.value = first ? first.value : toLocalDateString(new Date())
    }
    if (!scheduledTime.value) {
        setTimeFromMinutes(minTimeMinutes.value)
    }
    nextTick(() => updateDayScrollIndicators())
}

const dayScroller = ref<HTMLElement | null>(null)
const dayCanScrollLeft = ref(false)
const dayCanScrollRight = ref(false)

function updateDayScrollIndicators() {
    const el = dayScroller.value
    if (!el) {
        dayCanScrollLeft.value = false
        dayCanScrollRight.value = false
        return
    }
    dayCanScrollLeft.value = el.scrollLeft > 4
    dayCanScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
}

watch(orderTiming, async (val) => {
    if (val === 'scheduled') {
        await nextTick()
        updateDayScrollIndicators()
    }
})

onMounted(() => {
    nextTick(() => updateDayScrollIndicators())
    window.addEventListener('resize', updateDayScrollIndicators)
    window.addEventListener('storage', onStorageChange)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateDayScrollIndicators)
    window.removeEventListener('storage', onStorageChange)
})

watch(scheduledDate, () => {
    if (!scheduledTime.value) return
    if (currentTimeMinutes.value < minTimeMinutes.value || currentTimeMinutes.value > maxTimeMinutes.value) {
        setTimeFromMinutes(minTimeMinutes.value)
    }
})

const savedAddresses = ref<any[]>(JSON.parse(localStorage.getItem('saved_addresses') || '[]'))

const deliveryAddress = ref({
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    reference: '',
    lat: null as number | null,
    lng: null as number | null,
    ...(savedAddresses.value.length ? savedAddresses.value[0] : {})
})

const isAddressSearchOpen = ref(false)

function openAddressSearch() {
    isAddressSearchOpen.value = true
}

function onAddressSelected(addr: any) {
    deliveryAddress.value = {
        zipCode: addr.zipCode || '',
        street: addr.street || '',
        number: addr.number || '',
        complement: addr.complement || '',
        neighborhood: addr.neighborhood || '',
        city: addr.city || '',
        state: addr.state || '',
        reference: addr.reference || '',
        lat: addr.lat ?? null,
        lng: addr.lng ?? null
    }
    const existing = savedAddresses.value.findIndex((a: any) =>
        a.street === deliveryAddress.value.street &&
        a.number === deliveryAddress.value.number &&
        a.city === deliveryAddress.value.city
    )
    if (existing >= 0) {
        savedAddresses.value[existing] = { ...deliveryAddress.value }
    } else {
        savedAddresses.value.unshift({ ...deliveryAddress.value })
    }
    localStorage.setItem('saved_addresses', JSON.stringify(savedAddresses.value))
    isAddressSearchOpen.value = false
    showAddressForm.value = false
}

function onAddressManual() {
    isAddressSearchOpen.value = false
    showAddressForm.value = true
}

const cartMapEl = ref<HTMLDivElement | null>(null)
let cartMap: any = null

async function renderCartMapPreview() {
    const lat = deliveryAddress.value.lat
    const lng = deliveryAddress.value.lng
    if (lat === null || lng === null || lat === undefined || lng === undefined) return
    if (!cartMapEl.value) return
    try {
        await loadLeaflet()
    } catch {
        return
    }
    const L = (window as any).L
    if (!L || !cartMapEl.value) return

    if (cartMap) {
        try { cartMap.remove() } catch { /* noop */ }
        cartMap = null
    }
    cartMap = L.map(cartMapEl.value, {
        center: [lat, lng],
        zoom: 17,
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        touchZoom: false,
        boxZoom: false,
        keyboard: false,
        tap: false
    })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19
    }).addTo(cartMap)
}

watch(
    () => [deliveryAddress.value.lat, deliveryAddress.value.lng, showAddressForm.value, step.value],
    async () => {
        await nextTick()
        if (step.value === 2 && !showAddressForm.value && deliveryAddress.value.lat && deliveryAddress.value.lng) {
            renderCartMapPreview()
        } else if (cartMap) {
            try { cartMap.remove() } catch { /* noop */ }
            cartMap = null
        }
    },
    { deep: true }
)

const fetchAddressByCep = async () => {
    const cep = deliveryAddress.value.zipCode?.replace(/\D/g, '')
    if (cep?.length === 8) {
        try {
            const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
            const data = await res.json()
            if (!data.erro) {
                deliveryAddress.value.street = data.logradouro || ''
                deliveryAddress.value.neighborhood = data.bairro || ''
                deliveryAddress.value.city = data.localidade || ''
                deliveryAddress.value.state = data.uf || ''
            }
        } catch { /* ignore */ }
    }
}

const saveAddress = () => {
    if (!deliveryAddress.value.street || !deliveryAddress.value.number || !deliveryAddress.value.neighborhood || !deliveryAddress.value.city || !deliveryAddress.value.state) {
        toast.warning('Preencha os campos obrigatórios do endereço', { timeout: 2000 })
        return
    }
    const addr = { ...deliveryAddress.value }
    const existing = savedAddresses.value.findIndex((a: any) => a.zipCode === addr.zipCode && a.number === addr.number)
    if (existing >= 0) {
        savedAddresses.value[existing] = addr
    } else {
        savedAddresses.value.unshift(addr)
    }
    localStorage.setItem('saved_addresses', JSON.stringify(savedAddresses.value))
    showAddressForm.value = false
    toast.success('Endereço salvo!')
}

watch(() => restaurant.value.id, () => {
    cart.value = cart.value.filter((item: any) => item.restaurant_id == restaurant.value.id)
    localStorage.setItem('cart', JSON.stringify(cart.value))
})

watch(() => props.isOpen, (newValue) => {
    document.body.style.overflow = newValue ? 'hidden' : ''
    if (newValue) {
        tableNumber.value = localStorage.getItem('table_number')
    }
    if (!newValue) {
        step.value = 1
        openCouponForm.value = false
        showAddressForm.value = false
    }
})

watch(() => cart.value, (newCart: any) => {
    localStorage.setItem('cart', JSON.stringify(newCart))
}, { deep: true })

function getProductPrice(product: any) {
    if (!product) return 0
    const qty = Number(product.quantity || 1)
    const price = Number(product.price || 0)
    const promotional = product.promotionalPrice !== undefined && product.promotionalPrice !== null ? Number(product.promotionalPrice) : null
    const discount = Number(product.discount || 0)
    const discountType = product.discountType || product.discount_type

    let basePricePerUnit = (promotional !== null) ? promotional : price

    if (promotional === null && discount > 0) {
        if (discountType === 'amount' || discountType === 'fixed') {
            basePricePerUnit = price - discount
        } else if (discountType === 'percent' || discountType === 'percentage') {
            basePricePerUnit = price - (price * (discount / 100))
        }
    }

    let totalPerUnit = basePricePerUnit

    if (product.variations) {
        try {
            const variations = typeof product.variations === 'string' ? JSON.parse(product.variations) : product.variations
            if (Array.isArray(variations)) {
                variations.forEach((variation: any) => {
                    if (variation.values && Array.isArray(variation.values.prices)) {
                        variation.values.prices.forEach((p: any) => {
                            totalPerUnit += Number(p || 0)
                        })
                    }
                })
            }
        } catch { /* ignore */ }
    }

    const addonGroups = product.addonGroups || product.addon_groups
    if (addonGroups && Array.isArray(addonGroups)) {
        addonGroups.forEach((group: any) => {
            if (group.addons && Array.isArray(group.addons)) {
                group.addons.forEach((addon: any) => {
                    const addonQty = Number(addon.quantity || 0)
                    const addonPrice = Number(addon.price || 0)
                    totalPerUnit += (addonQty * addonPrice)
                })
            }
        })
    }

    const finalTotal = totalPerUnit * qty
    return isNaN(finalTotal) ? 0 : finalTotal
}

const applyCoupon = (): void => {
    if (couponCode.value.trim()) {
        appliedCoupon.value = {
            code: couponCode.value.trim(),
            discount: 0,
            discountType: 'fixed',
            minimumPurchasePrice: 0
        } as CouponModel
        openCouponForm.value = false
    } else {
        appliedCoupon.value = null
        openCouponForm.value = false
    }
}

const totalPrice = computed<number>(() => {
    if (restaurant.value.name) {
        return cart.value.reduce((acc: number, product: any) => acc + getProductPrice(product), 0)
    }
    return 0
})

const couponDiscount = computed<number>(() => {
    if (!appliedCoupon.value) return 0
    const total = totalPrice.value
    const discount = Number(appliedCoupon.value.discount || 0)
    const discountType = appliedCoupon.value.discountType || (appliedCoupon.value as any).discount_type

    if (discountType === 'fixed' || discountType === 'amount') {
        return Math.min(discount, total)
    } else if (discountType === 'percentage' || discountType === 'percent') {
        return total * (discount / 100)
    }
    return 0
})

const canProceed = computed(() => {
    if (!cart.value.length) return false
    if (isOnTab.value) {
        if (restaurant.value.minimumOrder > totalPrice.value) return false
        return true
    }
    if (step.value === 1) {
        if (restaurant.value.minimumOrder > totalPrice.value) return false
        return true
    }
    if (step.value === 2) {
        if (!deliveryType.value) return false
        if (deliveryType.value === 'delivery' && !deliveryAddress.value.street) return false
        if (orderTiming.value === 'scheduled' && !scheduledAt.value) return false
        return true
    }
    if (step.value === 3) {
        if (!paymentMethod.value) return false
        if (paymentMethod.value === 'cash' && changeAmount.value !== null && changeAmount.value < (totalPrice.value - couponDiscount.value)) return false
        return true
    }
    return false
})

const primaryActionLabel = computed(() => {
    if (isOnTab.value) return 'Enviar para a comanda'
    if (step.value === 3) return 'Finalizar pedido'
    return 'Continuar'
})

const deliveryTypeLabel = computed(() => {
    const found = deliveryTypes.value.find(t => t.value === deliveryType.value)
    return found?.label ?? '—'
})

const orderTimingLabel = computed(() => {
    if (orderTiming.value !== 'scheduled') return 'Para agora'
    if (!scheduledAt.value) return 'Agendado'
    const [d, t] = scheduledAt.value.split('T')
    const [, m, day] = d.split('-')
    return `${day}/${m} às ${t?.slice(0, 5)}`
})

const changeAmountDisplay = computed(() => {
    if (changeAmount.value === null || changeAmount.value === undefined) return ''
    return changeAmount.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
})

function onChangeAmountInput(e: Event) {
    const target = e.target as HTMLInputElement
    const digits = target.value.replace(/\D/g, '')
    if (!digits) {
        changeAmount.value = 0
        return
    }
    changeAmount.value = parseInt(digits, 10) / 100
}

function toggleChange(enabled: boolean) {
    if (enabled) {
        const min = totalPrice.value - couponDiscount.value
        changeAmount.value = Math.ceil(min)
    } else {
        changeAmount.value = null
    }
}

function paymentMethodIconBg(value: string) {
    if (value === 'pix') return 'bg-emerald-50 text-emerald-600'
    if (value === 'cash') return 'bg-emerald-50 text-emerald-600'
    return 'bg-gray-100 text-gray-800'
}

function goBack() {
    if (step.value === 1) {
        props.setIsOpen(false)
    } else {
        step.value = (step.value - 1) as 1 | 2 | 3
    }
}

function onPrimaryAction() {
    if (!canProceed.value) return
    if (isOnTab.value) {
        if (!customer.value.cpf) {
            isAuthModalOpen.value = true
            return
        }
        placeOrder()
        return
    }
    if (step.value === 1) {
        if (!customer.value.cpf) {
            isAuthModalOpen.value = true
            return
        }
        step.value = 2
        return
    }
    if (step.value === 2) {
        step.value = 3
        return
    }
    placeOrder()
}

async function placeOrder(): Promise<void> {
    if (!customer.value.cpf) {
        isAuthModalOpen.value = true
        return
    }

    if (appliedCoupon.value && appliedCoupon.value.minimumPurchasePrice > totalPrice.value) {
        toast.warning(`O pedido mínimo do cupom é de ${appliedCoupon.value!.minimumPurchasePrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, { timeout: 1500 })
        return
    }

    const orderType = isOnTab.value ? 'presencial' : (deliveryType.value || 'presencial')

    if (orderType === 'presencial') {
        const storedTableNumber = localStorage.getItem('table_number')
        if (!storedTableNumber || isNaN(parseInt(storedTableNumber))) {
            toast.warning('Escaneie uma mesa para fazer um pedido presencial')
            isQrcodeReaderOpen.value = true
            return
        }
    }

    if (orderType === 'delivery' && !deliveryAddress.value.street) {
        toast.warning('Preencha o endereço de entrega', { timeout: 2000 })
        showAddressForm.value = true
        return
    }

    if (!isOnTab.value && orderTiming.value === 'scheduled' && !scheduledAt.value) {
        toast.warning('Selecione data e horário para o agendamento', { timeout: 2000 })
        return
    }

    if (!isOnTab.value && paymentMethod.value === 'cash' && changeAmount.value && changeAmount.value > 0) {
        const finalTotal = totalPrice.value - couponDiscount.value
        if (changeAmount.value < finalTotal) {
            toast.warning('O valor para troco deve ser maior ou igual ao total do pedido', { timeout: 2000 })
            return
        }
    }

    if (couponCode.value && appliedCoupon.value?.code != couponCode.value) {
        applyCoupon()
    }

    isPlacingOrder.value = true

    const cleanCpf = customer.value.cpf.replace(/\D/g, '')

    // O telefone pode vir com o DDI 55 já salvo (retornado pelo backend em consultas anteriores);
    // a validação da API só aceita o número local (10 ou 11 dígitos), então removemos o prefixo aqui.
    const phoneDigits = (customer.value.phone || '').replace(/\D/g, '')
    const cleanPhone = phoneDigits.length > 11 && phoneDigits.startsWith('55')
        ? phoneDigits.slice(2)
        : phoneDigits

    const products = cart.value.map((itemValue: any) => {
        const item = JSON.parse(JSON.stringify(itemValue))

        const variants: { id: number; quantity: number }[] = []
        const variations = item.variations ? (typeof item.variations === 'string' ? JSON.parse(item.variations) : item.variations) : []

        if (Array.isArray(variations)) {
            variations.forEach((v: any) => {
                if (v.values && Array.isArray(v.values.ids)) {
                    v.values.ids.forEach((id: number) => {
                        variants.push({ id, quantity: 1 })
                    })
                }
            })
        }

        const itemAddOns: { id: number; quantity: number }[] = []
        const addonGroups = item.addon_groups || item.addonGroups || []

        if (Array.isArray(addonGroups)) {
            addonGroups.forEach((group: any) => {
                if (group.addons && Array.isArray(group.addons)) {
                    group.addons.forEach((addon: any) => {
                        itemAddOns.push({ id: addon.id, quantity: addon.quantity })
                    })
                }
            })
        }

        return {
            id: item.id,
            quantity: item.quantity,
            variants,
            addOns: itemAddOns
        }
    })

    const finalTotal = totalPrice.value - couponDiscount.value

    let addressId: number | null = null
    if (orderType === 'delivery') {
        try {
            const addrRes = await axiosClient.post('customer/addresses', {
                cpf: cleanCpf,
                name: customer.value.name,
                email: customer.value.email,
                street: deliveryAddress.value.street,
                number: deliveryAddress.value.number,
                complement: deliveryAddress.value.complement || null,
                neighborhood: deliveryAddress.value.neighborhood,
                city: deliveryAddress.value.city,
                state: deliveryAddress.value.state,
                zipCode: deliveryAddress.value.zipCode || null,
                reference: deliveryAddress.value.reference || null,
                lat: deliveryAddress.value.lat ?? null,
                lng: deliveryAddress.value.lng ?? null
            })
            addressId = addrRes.data?.id ?? addrRes.data?.data?.id ?? null
        } catch (error: any) {
            const apiErrors = error.response?.data?.errors
            if (Array.isArray(apiErrors) && apiErrors.length > 0) {
                apiErrors.forEach((e: any) => toast.error(e.message))
            } else {
                toast.error('Falha ao salvar endereço')
            }
            isPlacingOrder.value = false
            return
        }
        if (!addressId) {
            toast.error('Falha ao salvar endereço')
            isPlacingOrder.value = false
            return
        }
    }

    const orderPayload: any = {
        couponCode: appliedCoupon.value?.code,
        name: customer.value.name,
        email: customer.value.email,
        cpf: cleanCpf,
        phone: cleanPhone || undefined,
        notes: notes.value,
        products,
        deliveryType: orderType,
        tabNumber: orderType === 'presencial' ? localStorage.getItem('table_number') : null,
        cmFirebaseToken: cmFirebaseToken.value,
        addressId: addressId ?? undefined
    }

    if (!isOnTab.value) {
        const payments: any[] = []
        if (paymentMethod.value) {
            const payment: any = {
                method: paymentMethod.value,
                value: finalTotal,
                type: 'offline'
            }
            if (paymentMethod.value === 'cash' && changeAmount.value && changeAmount.value > 0) {
                payment.cash = { changeFor: changeAmount.value }
            }
            payments.push(payment)
        }
        orderPayload.paymentMethod = paymentMethod.value
        orderPayload.payments = payments
        orderPayload.timing = orderTiming.value
        orderPayload.scheduled = orderTiming.value === 'scheduled' && scheduledAt.value
            ? { deliveryDateTimeStart: `${scheduledAt.value}-03:00` }
            : null
    }

    axiosClient.post('customer/orders', orderPayload).then(() => {
        props.setIsOpen(false)
        cart.value = []
        step.value = 1
        toast.success('Pedido enviado')
        localStorage.setItem('customer', JSON.stringify({ ...customer.value, is_registered: true }))
        customer.value = { ...customer.value, is_registered: true }

        // O primeiro pedido cria o cliente na API; busca o cadastro para obter o uuid,
        // que é o que permite registrar o aparelho para notificações push
        if (!customer.value.uuid && customer.value.cpf) {
            axiosClient.get(`/customer/cpf/${customer.value.cpf.replace(/\D/g, '')}`).then((res: any) => {
                const updatedCustomer = { ...customer.value, ...res.data.customer, is_registered: true }
                localStorage.setItem('customer', JSON.stringify(updatedCustomer))
                customer.value = updatedCustomer
            }).catch(() => {})
        }
    }).catch((error) => {
        const apiErrors = error.response?.data?.errors
        if (Array.isArray(apiErrors) && apiErrors.length > 0) {
            apiErrors.forEach((e: any) => toast.error(e.message))
        } else if (error.response?.data?.message) {
            toast.error(error.response.data.message)
        } else {
            toast.error('Falha ao salvar pedido')
        }
    }).finally(() => {
        isPlacingOrder.value = false
    })
}
</script>

<style scoped lang="scss">
.cart-sidebar {
    --primary-color: v-bind("restaurant.restaurantDesignCustom?.primaryColor ?? '#ef4444'");
    background: #fff;
    width: 33.333%;
    /* No explicit height. With position:fixed + top:0 + bottom:0 the browser
       auto-sizes the panel to the *visible* viewport, which correctly tracks
       the mobile URL bar showing/hiding. Adding height:100vh would override
       bottom:0 and push the footer below the visible area on iOS/Android. */
    overflow: hidden;
}

@media (max-width: 1284px) {
    .cart-sidebar {
        width: 60%;
    }
}

@media (max-width: 600px) {
    .cart-sidebar {
        width: 100%;
    }
}

.cart-footer {
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0));
}

.scroll-snap-x {
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
}
.scroll-snap-x::-webkit-scrollbar {
    display: none;
}
.scroll-snap-x > button {
    scroll-snap-align: start;
}

input[type="date"]::-webkit-calendar-picker-indicator,
input[type="time"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

</style>
